"use client";

import React, { useEffect, useState } from "react";

const CODE_LINES = [
  { tokens: [{ text: "#include", type: "keyword" }, { text: " <stdio.h>", type: "string" }] },
  { tokens: [] },
  { tokens: [{ text: "int", type: "keyword" }, { text: " main", type: "fn" }, { text: "(void) {", type: "base" }] },
  { tokens: [{ text: '  printf', type: "fn" }, { text: "(", type: "base" }, { text: '"Hello Business Owner!"', type: "string" }, { text: ");", type: "base" }] },
  { tokens: [{ text: '  printf', type: "fn" }, { text: "(", type: "base" }, { text: '"Ready to make changes?"', type: "string" }, { text: ");", type: "base" }] },
  { tokens: [{ text: "  return", type: "keyword" }, { text: " 0;", type: "base" }] },
  { tokens: [{ text: "}", type: "base" }] },
];

const OUTPUT_LINES = ["Hello Business Owner!", "Ready to make changes?"];

const LINE_INTERVAL   = 110;
const OUTPUT_INTERVAL = 380;
const OUTPUT_DELAY    = 300;
const HOLD_AFTER_DONE = 1800;
const RESET_DURATION  = 400;

function SkeletonLine({ width, height = 10, delay = 0 }: { width: string; height?: number; delay?: number }) {
  return (
    <div style={{ width, height, borderRadius: 2, animationDelay: `${delay}ms` }} className="skel-line" />
  );
}

function SkeletonBlock({ children }: { children: React.ReactNode }) {
  return <div className="skel-block">{children}</div>;
}

export function PlaceHolderPanel({ className = "", label = "Placeholder" }: { className?: string; label?: string }) {
  const [visibleLines,  setVisibleLines]  = useState(0);
  const [visibleOutput, setVisibleOutput] = useState(0);
  const [cursorOn,      setCursorOn]      = useState(true);
  const [fading,        setFading]        = useState(false);
  const [cycle,         setCycle]         = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCursorOn((v) => !v), 530);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setVisibleLines(0);
    setVisibleOutput(0);
    setFading(false);

    const timers: ReturnType<typeof setTimeout>[] = [];

    for (let i = 1; i <= CODE_LINES.length; i++) {
      timers.push(setTimeout(() => setVisibleLines(i), i * LINE_INTERVAL));
    }

    const codeFinishAt = CODE_LINES.length * LINE_INTERVAL + OUTPUT_DELAY;

    for (let i = 1; i <= OUTPUT_LINES.length; i++) {
      timers.push(setTimeout(() => setVisibleOutput(i), codeFinishAt + i * OUTPUT_INTERVAL));
    }

    const allDoneAt = codeFinishAt + OUTPUT_LINES.length * OUTPUT_INTERVAL;

    timers.push(setTimeout(() => setFading(true), allDoneAt + HOLD_AFTER_DONE));
    timers.push(setTimeout(() => setCycle((c) => c + 1), allDoneAt + HOLD_AFTER_DONE + RESET_DURATION));

    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');

        .panel-root {
          width: 100%;
          height: 480px;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid #e2e2e3;
          font-family: 'DM Mono', monospace;
          overflow: hidden;
          transition: opacity ${RESET_DURATION}ms ease;
        }
        .panel-root.fading { opacity: 0; }

        .code-panel {
          height: 300px;
          background: #1a1c1d;
          padding: 20px 24px;
          display: block;
          overflow: hidden;
          border-bottom: 1px solid #2f3132;
          position: relative;
        }

        .top-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 22px; }
        .dot { width: 9px; height: 9px; border-radius: 50%; border: 1px solid #474747; }
        .filename { font-size: 11px; color: #c6c6c6; margin-left: 8px; letter-spacing: 0.06em; }

        .line-number { display: inline-block; width: 22px; font-size: 11px; color: #848484; text-align: right; margin-right: 18px; }
        .code-line { font-size: 12.5px; line-height: 1.9; min-height: 1.9em; opacity: 0; transform: translateX(-4px); transition: opacity 0.18s ease, transform 0.18s ease; }
        .code-line.visible { opacity: 1; transform: translateX(0); }

        .t-keyword { color: #c6c5cf; }
        .t-string  { color: #e2e2e2; }
        .t-fn      { color: #ffffff; font-weight: 500; }
        .t-base    { color: #cfc4c5; }

        .output-area { margin-top: 10px; padding-top: 10px; border-top: 1px solid #2f3132; }
        .output-line { font-size: 12px; line-height: 1.8; color: #e2e2e3; opacity: 0; transition: opacity 0.3s ease; }
        .output-line.visible { opacity: 1; }
        .prompt { color: #848484; margin-right: 10px; }
        .cursor { display: inline-block; width: 7px; height: 13px; background: #ffffff; vertical-align: middle; margin-left: 2px; transition: opacity 0.1s; }

        .skel-panel { height: 180px; background: #eeeeef; padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; overflow: hidden; }
        .skel-line { background: #dadadb; position: relative; overflow: hidden; }
        .skel-line::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, #f9f9fa 50%, transparent 100%); animation: shimmer 2s infinite; }
        .skel-block { background: #ffffff; border: 1px solid #e2e2e3; border-radius: 3px; padding: 12px 14px; }
        .row-2 { display: grid; grid-template-columns: 1fr 2fr; gap: 10px; }
        .row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        .avatar-skel { width: 30px; height: 30px; border-radius: 50%; background: #dadadb; }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        .divider { height: 1px; background: #e2e2e3; }
      `}</style>

      <div className={`panel-root ${className}${fading ? " fading" : ""}`}>

        <div className="code-panel">
          <div className="top-bar">
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
            <span className="filename">{label}</span>
          </div>

          {CODE_LINES.map((line, i) => (
            <div key={`${cycle}-${i}`} className={`code-line${visibleLines > i ? " visible" : ""}`}>
              <span className="line-number">{i + 1}</span>
              {line.tokens.map((t, j) => (
                <span key={j} className={`t-${t.type}`}>{t.text}</span>
              ))}
            </div>
          ))}

          {visibleLines >= CODE_LINES.length && (
            <div className="output-area">
              {OUTPUT_LINES.map((text, i) => (
                <div key={`${cycle}-out-${i}`} className={`output-line${visibleOutput > i ? " visible" : ""}`}>
                  <span className="prompt">$</span>
                  {text}
                  {visibleOutput === i + 1 && i === OUTPUT_LINES.length - 1 && (
                    <span className="cursor" style={{ opacity: cursorOn ? 1 : 0 }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="skel-panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ width: 6, height: 6, border: '1px solid #4c4546', transform: 'rotate(45deg)' }} />
            <span style={{ fontSize: 10, color: '#4c4546', letterSpacing: '0.12em' }}>UI UNDER CONSTRUCTION</span>
          </div>

          <div className="row-2">
            <SkeletonBlock><SkeletonLine width="55%" height={9} delay={0} /></SkeletonBlock>
            <SkeletonBlock><SkeletonLine width="75%" height={9} delay={100} /></SkeletonBlock>
          </div>

          <div className="row-3">
            {[0, 1, 2].map((i) => (
              <SkeletonBlock key={i}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SkeletonLine width="40%" height={8}  delay={i * 80} />
                  <SkeletonLine width="65%" height={13} delay={i * 80 + 60} />
                </div>
              </SkeletonBlock>
            ))}
          </div>

          <SkeletonBlock>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="avatar-skel" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <SkeletonLine width="28%" height={8} delay={200} />
                <SkeletonLine width="50%" height={8} delay={280} />
              </div>
              <div style={{ width: 52, height: 22, borderRadius: 2, background: '#dadadb' }} />
            </div>
          </SkeletonBlock>

          <div className="divider" />

          <SkeletonBlock>
            <SkeletonLine width="100%" height={9} delay={350} />
          </SkeletonBlock>
        </div>

      </div>
    </>
  );
}

export default PlaceHolderPanel;